import { useState } from 'react';
import axios from 'axios';
import { ArrowUp, ArrowDown, Pencil, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

export const FLOOR_KINDS = [
  { value: 'sous_sol', label: 'Sous-sol' },
  { value: 'courant', label: 'Courant' },
  { value: 'toit', label: 'Toit' },
];

const kindOf = (f) => f.kind || (f.level < 0 ? 'sous_sol' : 'courant');

/** V17.4 — nom éditable en ligne (clic crayon / double-clic), Entrée valide, Échap annule. */
export const FloorNameInline = ({ floor, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(floor.name);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const name = value.trim();
    if (!name || name === floor.name) { setEditing(false); setValue(floor.name); return; }
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/floors/${floor.floor_id}`, { name }, opts);
      onSaved(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Renommage impossible');
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1 min-w-0" onClick={(e) => e.stopPropagation()}>
        <span className="font-medium text-gray-700 truncate" onDoubleClick={() => setEditing(true)} data-testid={`floor-name-${floor.floor_id}`}>{floor.name}</span>
        <button type="button" className="p-0.5 text-gray-400 hover:text-gray-700 rounded" title="Renommer"
          onClick={() => { setValue(floor.name); setEditing(true); }} data-testid={`floor-rename-btn-${floor.floor_id}`}>
          <Pencil className="w-3 h-3" />
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} className="h-7 w-40 text-sm"
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setValue(floor.name); } }}
        data-testid={`floor-name-input-${floor.floor_id}`} />
      <button type="button" className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" onClick={save} disabled={saving} data-testid={`floor-name-save-${floor.floor_id}`}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </button>
      <button type="button" className="p-1 text-gray-400 hover:bg-gray-100 rounded" onClick={() => { setEditing(false); setValue(floor.name); }} data-testid={`floor-name-cancel-${floor.floor_id}`}>
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
};

/** Sélecteur de type d'étage compact (sous-sol · courant · toit). */
export const FloorKindInline = ({ floor, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const tone = { sous_sol: 'bg-slate-200 text-slate-700', courant: 'bg-gray-100 text-gray-600', toit: 'bg-amber-100 text-amber-800' };
  const change = async (kind) => {
    if (kind === kindOf(floor)) return;
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/floors/${floor.floor_id}`, { kind }, opts);
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Changement de type impossible');
    } finally { setSaving(false); }
  };
  return (
    <select value={kindOf(floor)} onChange={(e) => change(e.target.value)} disabled={saving}
      onClick={(e) => e.stopPropagation()} title="Type d'étage"
      className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 border-0 cursor-pointer focus:ring-1 focus:ring-blue-400 ${tone[kindOf(floor)]}`}
      data-testid={`floor-kind-select-${floor.floor_id}`}>
      {FLOOR_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
    </select>
  );
};

/** Flèches monter / descendre : échange le niveau avec l'étage voisin. */
export const FloorMoveButtons = ({ floor, isFirst, isLast, onMoved }) => {
  const [busy, setBusy] = useState(false);
  const move = async (direction) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/floors/${floor.floor_id}/move`, { direction }, opts);
      onMoved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Déplacement impossible');
    } finally { setBusy(false); }
  };
  const cls = 'p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent';
  return (
    <span className="inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      <button type="button" className={cls} disabled={busy || isLast} title="Monter d'un niveau" onClick={() => move('up')} data-testid={`floor-move-up-${floor.floor_id}`}>
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <button type="button" className={cls} disabled={busy || isFirst} title="Descendre d'un niveau" onClick={() => move('down')} data-testid={`floor-move-down-${floor.floor_id}`}>
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
    </span>
  );
};
